import { MSG, dequantPlayer, type Transport } from '@fafi/shared';
import { SnapshotBuffer } from './snapshotBuffer';

// Client connection state machine (§13.1 / §10.2). Wraps the Transport; on a WS drop it enters
// RECONNECTING (the MATCH screen stays mounted, showing the "reconnecting" overlay, inputs suppressed),
// and periodically resumes with the gameSessionToken. The server re-attaches the same entity and pushes
// a FULL snapshot; on receipt the client rebases its own entity and returns to PLAYING (§12.3, T-CLI-010).

export type ConnState = 'CONNECTING' | 'PLAYING' | 'RECONNECTING';
export const RECONNECT_INTERVAL_MS = 500;
/** D62: backoff ceiling. Retrying twice a second for a whole outage leaked sockets into the server's
 *  per-IP concurrency cap and locked the player out; the first retries stay fast, the tail backs off. */
export const RECONNECT_MAX_INTERVAL_MS = 8000;

export class Connection {
  state: ConnState = 'CONNECTING';
  overlayShown = false;
  overlayEverShown = false; // latch — proves the overlay was shown during the outage
  droppedAtMs = -1;
  resumedAtMs = -1;
  reconnectAttempts = 0;
  ownPos: { x: number; y: number } | null = null; // latest authoritative own-entity pos (the rebase anchor)
  snapshotsSeen = 0;
  private snaps = new SnapshotBuffer();
  private lastAttemptMs = -Infinity;

  constructor(
    transport: Transport,
    readonly ownId: number,
    private readonly resumeFn: () => Transport, // opens a fresh transport + asks the server to resume this session
    private readonly now: () => number,
  ) {
    this.attach(transport);
  }

  /** Inputs are suppressed while not PLAYING (§13.1 — interpolation frozen, overlay up). */
  get inputsEnabled(): boolean {
    return this.state === 'PLAYING';
  }

  private attach(t: Transport): void {
    t.onMessage((b) => this.onMessage(b));
    t.onClose(() => this.onClose());
  }

  private onMessage(bytes: Uint8Array): void {
    if (bytes.length === 0) return;
    if (bytes[0] === MSG.SNAPSHOT) {
      let snap;
      try {
        snap = this.snaps.ingest(bytes);
      } catch {
        return; // a stale delta with no baseline post-resume — wait for the full
      }
      this.snapshotsSeen += 1;
      const own = snap.players.find((p) => p.id === this.ownId);
      if (own) this.ownPos = dequantPlayer(own).pos; // rebase the own entity to authoritative truth
      if (this.state !== 'PLAYING') {
        this.state = 'PLAYING';
        this.overlayShown = false;
        if (this.resumedAtMs < 0 && this.droppedAtMs >= 0) this.resumedAtMs = this.now();
      }
    }
  }

  private onClose(): void {
    if (this.state === 'RECONNECTING') return;
    this.state = 'RECONNECTING';
    this.overlayShown = true;
    this.overlayEverShown = true;
    this.droppedAtMs = this.now();
    this.lastAttemptMs = -Infinity;
    this.snaps = new SnapshotBuffer(); // the resumed stream starts from a fresh full snapshot
  }

  /**
   * D62: the stream died while the socket stayed open (half-open TCP, a server that stopped serving
   * this session, a baseline the server no longer holds). Treat it exactly like a close — go
   * RECONNECTING with a fresh SnapshotBuffer so the resumed stream starts from a full snapshot — and
   * resume immediately rather than waiting out the interval.
   */
  forceResume(): void {
    if (this.state === 'RECONNECTING') return;
    this.onClose();
    this.lastAttemptMs = -Infinity;
  }

  /**
   * Drive the reconnect schedule on the virtual clock.
   *
   * D62: with EXPONENTIAL BACKOFF. The old fixed 500 ms retry had no backoff and no cap, so an outage
   * that a resume could not fix produced two sockets per second forever. Combined with the mux leaking
   * every superseded socket, that hit the server's 32-concurrent-per-IP cap in ~16 s, after which every
   * further attempt was refused with 1008 — the reconnect loop was itself sustaining the lockout.
   */
  tick(nowMs: number): void {
    if (this.state !== 'RECONNECTING') return;
    const delay = Math.min(RECONNECT_INTERVAL_MS * 2 ** Math.min(this.reconnectAttempts, 5), RECONNECT_MAX_INTERVAL_MS);
    if (nowMs - this.lastAttemptMs >= delay) {
      this.lastAttemptMs = nowMs;
      this.reconnectAttempts += 1;
      this.attach(this.resumeFn()); // new socket; server pushes a full snapshot → PLAYING on receipt
    }
  }

  /** D62: how long this outage has lasted — the caller escalates (JOIN fallback, then fatal) on it. */
  outageMs(nowMs: number): number {
    return this.state === 'RECONNECTING' && this.droppedAtMs >= 0 ? nowMs - this.droppedAtMs : 0;
  }
}
